import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Clock, CalendarCheck, AlertCircle, CheckCircle, ChevronLeft, ChevronRight, Download, Bell } from 'lucide-react';
import { format, parseISO, getISOWeek, startOfMonth, endOfMonth, addMonths, subMonths } from 'date-fns';
import toast from 'react-hot-toast';

import api from '../api/axios';
import Modal from '../components/common/Modal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { canStaffEdit, clockOutDate, monthStartOf, parseDeepLink } from '../constants/timeWindow';

function fmtTime(t) {
  if (!t) return '';
  // Extract HH:MM from either "HH:MM:SS" or "YYYY-MM-DD HH:MM:SS"
  return t.slice(-8, -3);
}

function fmtMinutes(mins) {
  if (!mins) return '—';
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function groupByWeek(shifts) {
  const groups = {};
  for (const shift of shifts) {
    const d = parseISO(shift.date);
    const weekNum = getISOWeek(d);
    const year = format(d, 'yyyy');
    const key = `${year}-W${String(weekNum).padStart(2, '0')}`;
    if (!groups[key]) groups[key] = { label: `Week ${weekNum} · ${year}`, shifts: [] };
    groups[key].shifts.push(shift);
  }
  return Object.values(groups);
}

// ── ClockModal ────────────────────────────────────────────────────────────────
function ClockModal({ slot, timeEntry, onClose, onSave, saving }) {
  const staffCanEdit = slot ? canStaffEdit(slot) : true;
  const existingIn  = timeEntry?.clocked_in_at  ? fmtTime(timeEntry.clocked_in_at)  : '';
  const existingOut = timeEntry?.clocked_out_at ? fmtTime(timeEntry.clocked_out_at) : '';

  const [inTime,  setInTime]  = useState(existingIn  || (slot ? slot.start_time.slice(0, 5) : ''));
  const [outTime, setOutTime] = useState(existingOut || (slot ? slot.end_time.slice(0, 5)   : ''));
  const [breakMins, setBreakMins] = useState(timeEntry?.break_minutes ?? 0);
  // For existing entries, mark as edited so auto-suggest never fires and overwrites the saved value
  const [breakEdited, setBreakEdited] = useState(Boolean(timeEntry?.id));

  // Auto-suggest break per German Working Hours Act when times change
  useEffect(() => {
    if (breakEdited) return;
    if (!inTime || !outTime) return;
    const [ih, im] = inTime.split(':').map(Number);
    const [oh, om] = outTime.split(':').map(Number);
    const diff = (oh * 60 + om) - (ih * 60 + im);
    const raw = diff > 0 ? diff : diff + 1440; // overnight
    if (raw > 540) setBreakMins(45);
    else if (raw > 360) setBreakMins(30);
    else setBreakMins(0);
  }, [inTime, outTime, breakEdited]);

  const rawMins = (() => {
    if (!inTime || !outTime) return null;
    const [ih, im] = inTime.split(':').map(Number);
    const [oh, om] = outTime.split(':').map(Number);
    const diff = (oh * 60 + om) - (ih * 60 + im);
    return diff > 0 ? diff : diff + 1440; // clock-out at/before clock-in = next day
  })();
  const netMins = rawMins != null ? Math.max(0, rawMins - breakMins) : null;

  if (!slot) return null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={timeEntry ? 'Update time entry' : 'Log work time'}
      size="sm"
    >
      <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '16px' }}>
        <strong>{slot.template_name || 'Shift'}</strong> ·{' '}
        {format(parseISO(slot.date), 'EEE, d MMM yyyy')} ·{' '}
        {slot.start_time.slice(0, 5)}–{slot.end_time.slice(0, 5)}
      </p>

      {!staffCanEdit && (
        <div style={{
          background: 'var(--warning-light)',
          border: '1px solid var(--warning)',
          borderRadius: 'var(--radius)',
          padding: '10px 12px',
          fontSize: '0.82rem',
          color: 'var(--text)',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <AlertCircle size={14} style={{ flexShrink: 0, color: 'var(--warning)' }} />
          Staff can log time only on the shift day and the day after. Only admins can change this shift.
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
            Clock In
          </label>
          <input
            type="time"
            className="input"
            value={inTime}
            onChange={(e) => setInTime(e.target.value)}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
            Clock Out
          </label>
          <input
            type="time"
            className="input"
            value={outTime}
            onChange={(e) => setOutTime(e.target.value)}
          />
        </div>
        <div style={{ width: '80px' }}>
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
            Break (min)
          </label>
          <input
            type="number"
            className="input"
            min="0"
            step="5"
            value={breakMins}
            onChange={(e) => { setBreakEdited(true); setBreakMins(Math.max(0, parseInt(e.target.value) || 0)); }}
          />
        </div>
      </div>

      <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '20px' }}>
        Net: <strong>{netMins ? fmtMinutes(netMins) : '—'}</strong>
        {rawMins && breakMins > 0 && (
          <span style={{ color: 'var(--text-muted)', marginLeft: '6px' }}>
            ({fmtMinutes(rawMins)} − {breakMins} min break)
          </span>
        )}
      </p>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
        <button
          className="btn btn-primary"
          onClick={() => onSave(
            slot,
            timeEntry,
            `${slot.date} ${inTime}:00`,
            outTime ? `${clockOutDate(slot.date, inTime, outTime)} ${outTime}:00` : null,
            breakMins,
          )}
          disabled={saving || !inTime}
        >
          {saving ? 'Saving…' : timeEntry ? 'Update' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

// ── NotLoggedList (admin) ───────────────────────────────────────────────────────
function NotLoggedList({ rows, loading, start, end, onLog }) {
  if (loading) return <LoadingSpinner label="Loading shifts…" />;
  const range = `${format(parseISO(start), 'd MMM')} – ${format(parseISO(end), 'd MMM yyyy')}`;
  if (rows.length === 0) {
    return (
      <div className="empty-state">
        <CheckCircle className="empty-state-icon" />
        <h3>Everything is logged</h3>
        <p>No past shifts without a time entry for {range}.</p>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>
        {rows.length} past {rows.length === 1 ? 'shift' : 'shifts'} without a time entry ({range}).
        Sick shifts, vacation and absences are excluded.
      </p>
      {groupByWeek(rows).map((group) => (
        <div key={group.label}>
          <h3 style={{
            fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)',
            textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px',
          }}>
            {group.label}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {group.shifts.map((row) => (
              <div
                key={`${row.id}-${row.user_id}`}
                className="card"
                style={{
                  padding: '14px 18px', borderLeft: '4px solid var(--warning)',
                  display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
                }}
              >
                <div style={{ minWidth: '130px' }}>
                  <p style={{ fontWeight: 600, fontSize: '0.9rem' }}>{format(parseISO(row.date), 'EEE, d MMM')}</p>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '2px' }}>
                    {row.start_time.slice(0, 5)}–{row.end_time.slice(0, 5)}
                  </p>
                </div>
                <div style={{ flex: 1, minWidth: '140px' }}>
                  <p style={{ fontWeight: 600, fontSize: '0.9rem' }}>{row.user_name}</p>
                  <p style={{ fontSize: '0.82rem', color: 'var(--primary-dark)', marginTop: '2px' }}>{row.template_name}</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {row.reminders_sent > 0 && (
                    <span className="badge badge-warning" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Bell size={11} /> {row.reminders_sent === 1 ? '1 reminder' : `${row.reminders_sent} reminders`}
                    </span>
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={() => onLog(row)}>
                    <Clock size={13} /> Log time
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── TimeTrackingPage ───────────────────────────────────────────────────────────
function TimeTrackingPage() {
  const { user, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deepLink] = useState(() => parseDeepLink(searchParams.toString()));
  const deepLinkHandled = useRef(false);

  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null); // null = self

  const [shifts, setShifts] = useState([]);
  const [entriesMap, setEntriesMap] = useState({}); // { [shift_slot_id]: entry }
  const [bonusMinutes, setBonusMinutes] = useState(0);
  const [loading, setLoading] = useState(true);

  const [clockModal, setClockModal] = useState(null); // { slot, timeEntry }
  const [clockSaving, setClockSaving] = useState(false);

  // Admin "Not logged" view (preset by the weekly digest link)
  const [view, setView] = useState(() => (deepLink.view === 'not-logged' ? 'not-logged' : 'shifts'));
  const [nlRange, setNlRange] = useState(() =>
    deepLink.view === 'not-logged' && deepLink.start && deepLink.end ? { start: deepLink.start, end: deepLink.end } : null
  );
  const [notLogged, setNotLogged] = useState([]);
  const [nlLoading, setNlLoading] = useState(false);

  // Timesheet self-serve download
  const [tsMonth, setTsMonth]   = useState(() => format(subMonths(new Date(), 1), 'yyyy-MM'));
  const [tsDlBusy, setTsDlBusy] = useState(false);

  const handleTsDownload = async () => {
    setTsDlBusy(true);
    try {
      const res = await api.get(
        `/reports/timesheet?month=${tsMonth}&user_id=${user?.id}`,
        { responseType: 'blob' }
      );
      const link = document.createElement('a');
      link.href = URL.createObjectURL(res.data);
      link.download = `stundennachweis-${tsMonth}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch {
      toast.error('Failed to download time sheet.');
    } finally {
      setTsDlBusy(false);
    }
  };

  // Fetch users list for admin user picker
  useEffect(() => {
    if (!isAdmin) return;
    api.get('/users').then((res) => setUsers(res.data)).catch(() => {});
  }, [isAdmin]);

  const targetUserId = isAdmin && selectedUserId ? selectedUserId : user?.id;

  const monthStr = format(currentMonth, 'yyyy-MM');
  const monthStart = format(currentMonth, 'yyyy-MM-dd');
  const monthEnd = format(endOfMonth(currentMonth), 'yyyy-MM-dd');

  const fetchData = useCallback(async () => {
    if (!targetUserId) return;
    setLoading(true);
    try {
      const [calRes, teRes, bonusRes] = await Promise.all([
        api.get(`/shifts/calendar?start=${monthStart}&end=${monthEnd}`),
        isAdmin && selectedUserId
          ? api.get(`/time-entries?user_id=${targetUserId}&start=${monthStart}&end=${monthEnd}`)
          : api.get(`/time-entries/me?start=${monthStart}&end=${monthEnd}`),
        api.get(`/sunday-bonus/hours?month=${monthStr}${isAdmin && selectedUserId ? `&user_id=${targetUserId}` : ''}`),
      ]);

      // Filter calendar slots to target user's assigned shifts (published)
      const userShifts = calRes.data.filter(
        (s) => s.assigned_user_id === targetUserId && s.period_status === 'published'
      );
      setShifts(userShifts);

      // Build lookup by shift_slot_id
      const map = {};
      for (const e of teRes.data) {
        map[e.shift_slot_id] = e;
      }
      setEntriesMap(map);

      setBonusMinutes(bonusRes.data.bonus_minutes ?? 0);
    } catch {
      toast.error('Failed to load time tracking data.');
    } finally {
      setLoading(false);
    }
  }, [targetUserId, monthStr, monthStart, monthEnd, isAdmin, selectedUserId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Deep link ?slot=<id> (reminder messages): open that month and its "Log time" modal
  useEffect(() => {
    if (deepLinkHandled.current || !deepLink.slotId || !user) return;
    deepLinkHandled.current = true;
    api.get(`/time-entries/slot/${deepLink.slotId}`)
      .then(({ data }) => {
        const { slot, entry } = data;
        if (slot.period_status !== 'published' || !slot.assigned_user_id) {
          toast.error('This shift is not open for time tracking.');
          return;
        }
        setView('shifts');
        setCurrentMonth(monthStartOf(slot.date));
        if (isAdmin && slot.assigned_user_id !== user.id) setSelectedUserId(slot.assigned_user_id);
        setClockModal({ slot, timeEntry: entry ?? null });
      })
      .catch((err) => toast.error(err.response?.data?.error || 'Could not open this shift.'))
      .finally(() => {
        // Drop ?slot= so a reload doesn't reopen the modal
        const next = new URLSearchParams(searchParams);
        next.delete('slot');
        setSearchParams(next, { replace: true });
      });
  }, [deepLink.slotId, user, isAdmin, searchParams, setSearchParams]);

  const nlStart = nlRange?.start ?? monthStart;
  const nlEnd = nlRange?.end ?? monthEnd;

  const fetchNotLogged = useCallback(async () => {
    if (!isAdmin || view !== 'not-logged') return;
    setNlLoading(true);
    try {
      const res = await api.get(`/time-entries/not-logged?start=${nlStart}&end=${nlEnd}`);
      setNotLogged(res.data.slots);
    } catch {
      toast.error('Failed to load shifts without time entry.');
    } finally {
      setNlLoading(false);
    }
  }, [isAdmin, view, nlStart, nlEnd]);

  useEffect(() => {
    fetchNotLogged();
  }, [fetchNotLogged]);

  const changeView = (v) => {
    setView(v);
    const next = new URLSearchParams(searchParams);
    if (v === 'not-logged') next.set('view', 'not-logged'); else { next.delete('view'); next.delete('start'); next.delete('end'); }
    setSearchParams(next, { replace: true });
  };

  // Month navigation replaces a range preset from the digest link
  const goMonth = (fn) => {
    setCurrentMonth(fn);
    if (nlRange) {
      setNlRange(null);
      const next = new URLSearchParams(searchParams);
      next.delete('start'); next.delete('end');
      setSearchParams(next, { replace: true });
    }
  };

  const handleClockSave = async (slot, existingEntry, clockedIn, clockedOut, breakMins = 0) => {
    setClockSaving(true);
    try {
      if (existingEntry) {
        await api.put(`/time-entries/${existingEntry.id}`, {
          clocked_in_at: clockedIn,
          clocked_out_at: clockedOut,
          break_minutes: breakMins,
        });
      } else {
        await api.post('/time-entries', {
          shift_slot_id: slot.id,
          clocked_in_at: clockedIn,
          clocked_out_at: clockedOut,
          break_minutes: breakMins,
        });
      }
      toast.success('Time entry saved.');
      setClockModal(null);
      fetchData();
      fetchNotLogged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save time entry.');
    } finally {
      setClockSaving(false);
    }
  };

  // Compute stats
  const allEntries = Object.values(entriesMap);
  const regularMinutes = allEntries
    .filter((e) => e.entry_type !== 'sick_leave')
    .reduce((s, e) => s + (e.total_minutes || 0), 0);
  const sickMinutes = allEntries
    .filter((e) => e.entry_type === 'sick_leave' && e.au_certificate_submitted) // sick hours only with AU
    .reduce((s, e) => s + (e.total_minutes || 0), 0);

  const groups = groupByWeek(shifts);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Time Tracking</h1>
          <p className="page-subtitle">Log and review clock times for your assigned shifts.</p>
        </div>
      </div>

      {/* Month navigator + admin user picker */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => goMonth((m) => subMonths(m, 1))}
            style={{ padding: '6px 8px' }}
          >
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontWeight: 600, fontSize: '0.95rem', minWidth: '120px', textAlign: 'center' }}>
            {format(currentMonth, 'MMMM yyyy')}
          </span>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => goMonth((m) => addMonths(m, 1))}
            style={{ padding: '6px 8px' }}
          >
            <ChevronRight size={16} />
          </button>
        </div>

        {isAdmin && (
          <div style={{ display: 'flex', gap: '4px' }}>
            {[{ key: 'shifts', label: 'Shifts' }, { key: 'not-logged', label: 'Not logged' }].map((v) => (
              <button
                key={v.key}
                className={`btn btn-sm ${view === v.key ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => changeView(v.key)}
              >
                {v.label}
              </button>
            ))}
          </div>
        )}

        {isAdmin && view === 'shifts' && users.length > 0 && (
          <select
            className="input"
            style={{ width: 'auto', minWidth: '180px' }}
            value={selectedUserId ?? ''}
            onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">My shifts</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        )}
      </div>

      {isAdmin && view === 'not-logged' && (
        <NotLoggedList
          rows={notLogged}
          loading={nlLoading}
          start={nlStart}
          end={nlEnd}
          onLog={(row) => setClockModal({ slot: row, timeEntry: null })}
        />
      )}

      {view === 'shifts' && <>
      {/* Stats row */}
      {!loading && (regularMinutes > 0 || sickMinutes > 0 || bonusMinutes > 0) && (
        <div className="grid-3" style={{ gap: '12px', marginBottom: '20px' }}>
          <div className="stat-card">
            <div className="stat-icon stat-icon-primary"><Clock size={20} /></div>
            <div className="stat-content">
              <p className="stat-label">Logged hours</p>
              <p className="stat-value" style={{ fontSize: '1.2rem' }}>{fmtMinutes(regularMinutes)}</p>
            </div>
          </div>
          {sickMinutes > 0 && (
            <div className="stat-card">
              <div className="stat-icon stat-icon-danger"><AlertCircle size={20} /></div>
              <div className="stat-content">
                <p className="stat-label">Sick leave</p>
                <p className="stat-value" style={{ fontSize: '1.2rem' }}>{fmtMinutes(sickMinutes)}</p>
              </div>
            </div>
          )}
          {bonusMinutes > 0 && (
            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#FFF3E0', color: '#E65100' }}>
                <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>+%</span>
              </div>
              <div className="stat-content">
                <p className="stat-label">Sunday bonus</p>
                <p className="stat-value" style={{ fontSize: '1.2rem' }}>{fmtMinutes(bonusMinutes)}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Shifts list */}
      {loading ? (
        <LoadingSpinner label="Loading shifts…" />
      ) : shifts.length === 0 ? (
        <div className="empty-state">
          <CalendarCheck className="empty-state-icon" />
          <h3>No shifts this month</h3>
          <p>No published shifts assigned for {format(currentMonth, 'MMMM yyyy')}.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {groups.map((group) => (
            <div key={group.label}>
              <h3 style={{
                fontSize: '0.85rem',
                fontWeight: 600,
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '10px',
              }}>
                {group.label}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {group.shifts.map((shift) => {
                  const entry = entriesMap[shift.id];
                  const hasOut = entry?.clocked_out_at;
                  const hasIn = entry?.clocked_in_at;

                  let statusBadge;
                  if (hasOut) {
                    const loggedLabel = `LOGGED · ${fmtTime(entry.clocked_in_at)}–${fmtTime(entry.clocked_out_at)}${entry.total_minutes ? ` (${fmtMinutes(entry.total_minutes)})` : ''}`;
                    statusBadge = (
                      <span className="badge badge-success" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontVariantNumeric: 'tabular-nums' }}>
                        <CheckCircle size={11} /> {loggedLabel}
                      </span>
                    );
                  } else if (hasIn) {
                    statusBadge = <span className="badge badge-warning">In progress · {fmtTime(entry.clocked_in_at)}</span>;
                  } else {
                    statusBadge = <span className="badge" style={{ background: 'var(--bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>Not logged</span>;
                  }

                  return (
                    <div
                      key={shift.id}
                      className="card"
                      style={{
                        padding: '14px 18px',
                        borderLeft: `4px solid ${entry ? 'var(--success)' : 'var(--border)'}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        flexWrap: 'wrap',
                      }}
                    >
                      {/* Date */}
                      <div style={{ minWidth: '130px' }}>
                        <p style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                          {format(parseISO(shift.date), 'EEE, d MMM')}
                        </p>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '2px' }}>
                          {shift.start_time.slice(0, 5)}–{shift.end_time.slice(0, 5)}
                        </p>
                      </div>

                      {/* Shift name */}
                      <div style={{ flex: 1 }}>
                        <p style={{ fontWeight: 500, color: 'var(--primary-dark)', fontSize: '0.9rem' }}>
                          {shift.template_name || 'Shift'}
                        </p>
                      </div>

                      {/* Status badge + action button */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {statusBadge}
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setClockModal({ slot: shift, timeEntry: entry ?? null })}
                        >
                          <Clock size={13} />
                          {entry ? 'Edit time' : 'Log time'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      </>}

      {/* Timesheet self-serve download */}
      {!isAdmin && (
        <div className="card" style={{ marginTop: '28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <p style={{ fontWeight: 600, fontSize: '0.92rem' }}>Download time sheet</p>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '2px' }}>
              Signed PDF for your own records.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="month"
              className="input"
              value={tsMonth}
              max={format(subMonths(new Date(), 1), 'yyyy-MM')}
              onChange={e => setTsMonth(e.target.value)}
              style={{ width: '160px' }}
            />
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleTsDownload}
              disabled={tsDlBusy || !user?.id}
            >
              <Download size={13} />
              {tsDlBusy ? 'Downloading…' : 'Download PDF'}
            </button>
          </div>
        </div>
      )}

      {clockModal && (
        <ClockModal
          slot={clockModal.slot}
          timeEntry={clockModal.timeEntry}
          onClose={() => setClockModal(null)}
          onSave={handleClockSave}
          saving={clockSaving}
        />
      )}
    </div>
  );
}

export default TimeTrackingPage;
